// The canvas the paper is drawn on. Lifted from the Andon app's `Confetti`,
// in the one size the shop floor needs: the whole screen, for the person who
// just closed the andon.
//
// IT NEVER TAKES A TAP. `pointerEvents: none` on the canvas, always — a
// celebration that swallowed the Send button for a second and a half would be
// the single worst thing in this app.
//
// REDUCED MOTION IS HONOURED BY DRAWING NOTHING. Not by a gentler animation:
// somebody who has asked their device for less movement has asked for less
// movement, and the andon is closed whether or not paper falls about it. Nothing
// downstream depends on this having run.

import { useEffect, useRef } from 'react';
import { makeParticles, opacityOf, stepParticles, type Particle } from '@/lib/confetti';

export function Confetti({
  fire,
}: {
  /**
   * A TOKEN, not a flag: it fires whenever this CHANGES to something non-zero.
   *
   * A boolean would need every caller to set it and unset it a moment later,
   * which is bookkeeping in three places and a burst that silently does not
   * happen the second time. A number that only ever goes up says "again"
   * without anybody having to remember to reset it — see `celebrate` on the
   * andon slice.
   */
  fire: number;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const lastFire = useRef(fire);
  /**
   * The frame in flight, so a second burst can cancel the first.
   *
   * NOT a "busy" flag. A flag has to be cleared by the loop that set it, and
   * the loop only runs while the tab is visible — so a burst fired into a
   * backgrounded tab could leave the flag set and silently swallow every
   * celebration afterwards. Cancelling and restarting has no state to get
   * stuck in.
   */
  const frame = useRef(0);

  useEffect(() => {
    const changed = fire !== lastFire.current;
    lastFire.current = fire;
    if (!changed || fire <= 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = canvas.current;
    if (!el) return;
    cancelAnimationFrame(frame.current);
    const ctx = el.getContext('2d');
    if (!ctx) return;

    // Sized from the box it is actually in, at the device's own pixel ratio —
    // a canvas left at its CSS size draws a blurry rectangle on every phone
    // made in the last decade.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const box = el.getBoundingClientRect();
    const width = Math.max(1, box.width);
    const height = Math.max(1, box.height);
    el.width = Math.round(width * dpr);
    el.height = Math.round(height * dpr);
    ctx.scale(dpr, dpr);

    // A little above the middle, thrown far enough to reach the corners of a
    // phone.
    let particles: Particle[] = makeParticles({ x: width / 2, y: height * 0.55, count: 90, power: 900, ttl: 2200 });
    let last = performance.now();

    const draw = (at: number) => {
      const dt = at - last;
      last = at;
      particles = stepParticles(particles, dt);
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        ctx.save();
        ctx.globalAlpha = opacityOf(p);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (particles.length > 0) frame.current = requestAnimationFrame(draw);
    };
    frame.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame.current);
      ctx.clearRect(0, 0, width, height);
    };
  }, [fire]);

  return (
    <canvas
      ref={canvas}
      // Decoration, and nothing else. The thing that actually happened is
      // already said in words — the toast — so there is nothing here for a
      // screen reader to miss.
      aria-hidden="true"
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 90 }}
    />
  );
}
